import { Component, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatStoreService } from '../../shared/chat-store.service';

@Component({
  standalone: true,
  selector: 'app-dashboard-page',
  imports: [CommonModule],
  template: `
  <div class="page">
    <div class="hero">
      <div class="ic">📊</div>
      <div>
        <div class="h1">Dashboard</div>
        <div class="h2">Inbox health snapshot computed from current conversation data</div>
      </div>
    </div>

    <div class="stats">
      <div class="stat"><div class="k">Conversations</div><div class="v">{{ all().length }}</div></div>
      <div class="stat"><div class="k">Human Takeover</div><div class="v">{{ humanRequiredCount() }}</div></div>
      <div class="stat"><div class="k">Billable Risk</div><div class="v">{{ billableCount() }}</div></div>
      <div class="stat"><div class="k">Realized Revenue</div><div class="v">{{ money(realizedRevenue()) }}</div></div>
    </div>

    <div class="grid">
      <div class="card">
        <div class="t">Category Mix</div>
        <div class="bars">
          <div class="bar">
            <span>Leads</span>
            <b>{{ byCategory('Lead') }}</b>
          </div>
          <div class="bar">
            <span>Orders</span>
            <b>{{ byCategory('Sales') }}</b>
          </div>
          <div class="bar">
            <span>Human Takeover</span>
            <b>{{ humanRequiredCount() }}</b>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="t">Stage Distribution</div>
        <div class="chips">
          <span class="chip" *ngFor="let s of stageBreakdown()">{{ s.stage }} · {{ s.count }}</span>
          <span class="m" *ngIf="!stageBreakdown().length">No stages found</span>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="t">Conversations Needing Attention</div>
      <div class="tableHead">
        <div>Conversation</div><div>Reason</div><div>Category</div><div>Stage</div>
      </div>
      <div class="row" *ngFor="let c of attentionList()">
        <div class="name">{{ c.displayName }}</div>
        <div>
          <span class="reason human" *ngIf="c.requiresHumanAgent">Human Required</span>
          <span class="reason bill" *ngIf="c.billableOnNextCustomerMessage">Billable Reply</span>
          <span class="reason manual" *ngIf="!c.botEnabled">Manual Mode</span>
        </div>
        <div class="muted">{{ categoryLabel(c.mainCategory) }}</div>
        <div class="muted">{{ c.stage || '-' }}</div>
      </div>
      <div class="m" *ngIf="!attentionList().length">No urgent conversations right now.</div>
    </div>
  </div>
  `,
  styles: [`
    .page{flex:1; padding:16px; overflow:auto}
    .hero{display:flex; gap:12px; align-items:center; padding:14px; border-radius:18px;
      border:1px solid var(--border); background:rgba(17,27,33,.55)}
    .ic{width:44px;height:44px;border-radius:16px;display:flex;align-items:center;justify-content:center;
      background:rgba(0,168,132,.16);border:1px solid rgba(0,168,132,.35);font-size:20px}
    .h1{font-size:18px;font-weight:900}
    .h2{margin-top:2px;color:var(--muted);font-size:12px}
    .stats{margin-top:12px;display:grid;gap:12px;grid-template-columns:repeat(4,minmax(0,1fr))}
    .stat{padding:12px;border-radius:16px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .k{font-size:12px;color:var(--muted)}
    .v{font-size:24px;font-weight:900;margin-top:4px}
    .grid{margin-top:12px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    .card{margin-top:12px;padding:14px;border-radius:18px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .t{font-weight:900}
    .m{margin-top:6px;color:var(--muted);font-size:12px;line-height:1.35}
    .bars{margin-top:10px;display:grid;gap:8px}
    .bar{display:flex;justify-content:space-between;padding:8px 10px;border-radius:12px;background:rgba(42,57,66,.35)}
    .chips{margin-top:10px;display:flex;gap:8px;flex-wrap:wrap}
    .chip{font-size:11px;padding:4px 8px;border-radius:999px;border:1px solid rgba(0,168,132,.35);background:rgba(0,168,132,.14)}
    .tableHead,.row{display:grid;grid-template-columns:1.4fr 1.6fr 1fr 1fr;gap:10px;align-items:center}
    .tableHead{margin-top:10px;padding:8px 10px;font-size:11px;color:var(--muted);font-weight:700;text-transform:uppercase}
    .row{padding:10px;border-radius:12px}
    .row:hover{background:rgba(42,57,66,.35)}
    .name{font-weight:800}
    .reason{font-size:10px;padding:2px 7px;border-radius:999px;border:1px solid transparent;font-weight:700;margin-right:6px}
    .reason.human{color:rgb(253,186,116);border-color:rgba(217,119,6,.45);background:rgba(217,119,6,.18)}
    .reason.bill{color:rgb(251,113,133);border-color:rgba(225,29,72,.45);background:rgba(225,29,72,.16)}
    .reason.manual{color:rgb(251,191,36);border-color:rgba(217,119,6,.42);background:rgba(217,119,6,.16)}
    .muted{color:var(--muted)}
    :host-context(body.light) .hero,
    :host-context(body.light) .stat,
    :host-context(body.light) .card{
      background:rgba(255,255,255,.92);
      border-color:rgba(15,23,42,.10);
    }
    :host-context(body.light) .row:hover,
    :host-context(body.light) .bar{background:rgba(15,23,42,.04)}
    :host-context(body.light) .chip{
      color:#0f766e;
      border-color:rgba(13,148,136,.24);
      background:rgba(20,184,166,.10);
    }
    :host-context(body.light) .h1,
    :host-context(body.light) .name,
    :host-context(body.light) .v,
    :host-context(body.light) .t{color:#0f172a}
    :host-context(body.light) .k,
    :host-context(body.light) .h2,
    :host-context(body.light) .m,
    :host-context(body.light) .muted,
    :host-context(body.light) .tableHead{color:#475569}
    :host-context(body.light) .bar b{color:#0f172a}
    :host-context(body.light) .reason.human{
      color:#9a3412;
      border-color:rgba(217,119,6,.35);
      background:rgba(251,146,60,.20);
    }
    :host-context(body.light) .reason.bill{
      color:#9f1239;
      border-color:rgba(225,29,72,.35);
      background:rgba(251,113,133,.18);
    }
    :host-context(body.light) .reason.manual{
      color:#92400e;
      border-color:rgba(217,119,6,.35);
      background:rgba(251,191,36,.20);
    }
    @media (max-width: 1100px){
      .stats{grid-template-columns:repeat(2,minmax(0,1fr))}
    }
    @media (max-width: 900px){
      .stats{grid-template-columns:1fr 1fr}
      .grid{grid-template-columns:1fr}
      .tableHead{display:none}
      .row{grid-template-columns:1fr}
    }
  `]
})
export class DashboardPageComponent {
  all = computed(() => this.store.allContacts());
  humanRequiredCount = computed(() => this.all().filter(c => c.requiresHumanAgent).length);
  billableCount = computed(() => this.all().filter(c => c.billableOnNextCustomerMessage).length);
  realizedRevenue = computed(() =>
    this.all()
      .filter(c => c.mainCategory === 'Sales' && c.orderStatus === 'paid')
      .reduce((sum, c) => sum + (c.orderValue || 0), 0)
  );

  attentionList = computed(() =>
    this.all().filter(c => c.requiresHumanAgent || c.billableOnNextCustomerMessage || !c.botEnabled)
  );

  constructor(private store: ChatStoreService) {}

  byCategory(category: 'Lead' | 'Sales'): number {
    return this.all().filter(c => c.mainCategory === category).length;
  }

  stageBreakdown(): Array<{ stage: string; count: number }> {
    const map = new Map<string, number>();
    for (const c of this.all()) {
      const key = c.stage || 'Unspecified';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()]
      .map(([stage, count]) => ({ stage, count }))
      .sort((a, b) => b.count - a.count);
  }

  categoryLabel(category?: string): string {
    if (category === 'Sales') return 'Orders';
    return category || '-';
  }

  money(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  }
}
